import { useEffect, useRef } from 'react';

/**
 * When active, applies classname(s) to the html element to prevent users from scrolling the page.
 * @param isActive Whether the lock is active
 * @param hidePageScrollbar This only impacts users that have scrollbars always on in there OS settings.
 * When false (default), the scrollbar gutter is maintained on the html element so that there is no change
 * in page width when the overflow styling is applied. When true, that gutter is not maintained.
 * This should never be true on explore as it causes the width of the map to change so new results are fetched.
 * @param transitionDelayMs Optional delay in milliseconds to wait before checking if modals are still in the portal.
 * This allows CSS transitions to complete. Only used when provided.
 */
const useScreenLock = (isActive: boolean, hidePageScrollbarGutter = false, transitionDelayMs?: number) => {
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const htmlDocument = document.querySelector('html') as HTMLElement;

    if (isActive) {
      htmlDocument.classList.add('modalOpen');
      if (hidePageScrollbarGutter) {
        htmlDocument.classList.add('hidePageScrollbarGutter');
      }
    }

    return () => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const checkAndRemoveClasses = () => {
        const count = document.getElementById('modalPortal')?.childElementCount;
        if (count === undefined || count < 1) {
          // Remove the class only if there are no more modals
          htmlDocument.classList.remove('modalOpen', 'hidePageScrollbarGutter');
        }
      };

      if (transitionDelayMs !== undefined) {
        // Use timeout logic when transition delay is provided
        timeoutRef.current = setTimeout(checkAndRemoveClasses, transitionDelayMs);
      } else {
        // Use immediate check when no transition delay is provided
        checkAndRemoveClasses();
      }
    };
  }, [hidePageScrollbarGutter, isActive, transitionDelayMs]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
};

export default useScreenLock;
